import type { BidType, PromotionTypeEnum } from "@krea/shared/types/common";

import type { IFieldRequest } from "./fieldRequest";
import type { ILenderApplication } from "./lenderApplication";

export enum LenderResponseTypeEnum {
  UNCONDITIONAL_BID = "UNCONDITIONAL_BID",
  CONDITIONAL_BID = "CONDITIONAL_BID",
}

export enum LenderResponseBenefitTypeEnum {
  NO_ADDITIONS_REQUIRED = "NO_ADDITIONS_REQUIRED",
  REDUCED_GUARANTOR_COMMITMENT = "REDUCED_GUARANTOR_COMMITMENT",
  NO_GUARANTOR_REQUIRED = "NO_GUARANTOR_REQUIRED",
  FREE_EARLY_PAYMENT = "FREE_EARLY_PAYMENT",
  NO_LEASING_DOWN_PAYMENT = "NO_LEASING_DOWN_PAYMENT",
  NO_LIMIT_FEE = "NO_LIMIT_FEE",
  NO_SETUP_FEE = "NO_SETUP_FEE",
  EU_LOAN = "EU_LOAN",
  NO_WITHDRAWAL_FEE = "NO_WITHDRAWAL_FEE",
  NO_RECOURSE = "NO_RECOURSE",
  NO_AMORTIZATION = "NO_AMORTIZATION",
}

export interface ILenderResponseBenefit {
  icon?: string;
  displayName: string;
  explanation: string;
  type: LenderResponseBenefitTypeEnum;
}

export interface IPromotion {
  type: PromotionTypeEnum;
  value: number;
}

export interface ICompanyLoanBid {
  type: BidType.COMPANY_LOAN;
  createdAt: string;
  id: string;

  earlyRepaymentFee: string | null;
  setupFee: number | null;
  postalFee: number | null;
  directDebitFee: number | null;
  isRepayableWithoutFee: boolean;

  amount: number;
  paybackTimeMonths: number;
  amortizationMethod: string;
  amortizationMethodDisplayName: string;
  amortizationMethodDescription: string;
  lenderMonthlyInterest: number | null;
  lenderMonthlyAmount: number | null;

  averageMonthlyPayment: number;
  averageMonthlyCost: number;
  firstMonthCost: number;
  averageMonthlyAmortisation: number;
  firstMonthAmortisation: number;
  comparableMonthlyInterestRate: number;
  comparableYearlyInterestRate: number;
  firstMonthInterestRate: number;
  firstMonthPayment: number;
}

export interface IFactoringBid {
  type: BidType.FACTORING;
  createdAt: string;
  id: string;

  postalFee: number | null;
  directDebitFee: number | null;
  setupFee: number | null;

  purchaseLimit: number;
  purchasedAmountPercent: number;
  paymentTermsDays: number;
  commissionPercent: number;
  withRegress: boolean;
  regressNumDaysAfterDueDate: number | null;
  limitFeePerYearPercent: number | null;
  handlingFeePerInvoice: number | null;
  monthlyFee: number | null;
  amount: number;
  averageMonthlyCost: number;
  firstMonthCost: number;
  comparableMonthlyInterestRate: number;
  comparableYearlyInterestRate: number;
  firstMonthInterestRate: number;
}

export interface ILeasingBid {
  type: BidType.LEASING;
  createdAt: string;
  id: string;

  postalFee: number | null;
  directDebitFee: number | null;
  setupFee: number | null;
  amount: number;
  paybackTimeMonths: number;
  downPayment: number;
  monthlyPayment: number;
  firstMonthPayment: number;
  terminalValue: number;
  contractType: string;
  insuranceMonthlyCost: number | null;
  firstMonthCost: number;
  averageMonthlyCost: number;
  averageMonthlyAmortisation: number;
  firstMonthAmortisation: number;
  comparableMonthlyInterestRate: number;
  comparableYearlyInterestRate: number;
  firstMonthInterestRate: number;
}

export interface IRealEstateBid {
  type: BidType.REAL_ESTATE;
  createdAt: string;
  id: string;

  earlyRepaymentFee: number | null;
  postalFee: number | null;
  directDebitFee: number | null;
  setupFee: number | null;

  amount: number;
  paybackTimeMonths: number;
  amortizationMethod: string;
  amortizationMethodDisplayName: string;
  amortizationMethodDescription: string;
  lenderMonthlyInterest: number | null;
  lenderMonthlyAmount: number | null;
  amortizationDuringTerm: number;
  averageMonthlyPayment: number;
  firstMonthCost: number;
  averageMonthlyCost: number;
  averageMonthlyAmortisation: number;
  firstMonthAmortisation: number;
  comparableMonthlyInterestRate: number;
  comparableYearlyInterestRate: number;
  firstMonthInterestRate: number;
  firstMonthPayment: number;
}

export interface IRevolvingCreditBid {
  type: BidType.REVOLVING_CREDIT;
  createdAt: string;
  id: string;

  earlyRepaymentFee: number | null;
  postalFee: number | null;
  directDebitFee: number | null;
  setupFee: number | null;

  amount: number;
  monthlyAmortisation: number;
  // using lenderMonthlyInterestRevolvingCredit via GraphQL alias instead of lenderMonthlyInterest, because of name/type conflicts in graphql
  lenderMonthlyInterestRevolvingCredit: number;
  withdrawalFee: number;
  limitFeePerYear: number | null;
  averageMonthlyPayment: number;
  firstMonthPayment: number;
  averageMonthlyCost: number;
  firstMonthCost: number;
  averageMonthlyAmortisation: number;
  firstMonthAmortisation: number;
  comparableMonthlyInterestRate: number;
  comparableYearlyInterestRate: number;
  firstMonthInterestRate: number;
}

// Used when bids data (for dashboard) have been fetched (deeper nested data included, such as .lenderResponse.lenderApplication.lenderCompany)
export type IResolvedBid =
  | ICompanyLoanBid
  | IFactoringBid
  | ILeasingBid
  | IRealEstateBid
  | IRevolvingCreditBid;

export interface ILenderResponse {
  id: string;
  updatedAt: string;
  createdAt: string;
  bids: IResolvedBid[];
  fieldRequests: IFieldRequest[];
  bid?: ICompanyLoanBid;
  invoicePurchaseBid?: IFactoringBid;
  leasingBid?: ILeasingBid;
  realEstateBid?: IRealEstateBid;
  revolvingCreditBid?: IRevolvingCreditBid;
  type: LenderResponseTypeEnum;
  benefits: ILenderResponseBenefit[];
  promotions?: IPromotion[];
  creditTermsFile?: {
    id: string;
    url: string;
    originalFileName: string;
    createdAt: DateString;
  };
  attractivenessScore?: number;
  lenderApplication: Omit<ILenderApplication, "loanApplication">;
  isRecommendedSelect: boolean;
  paidOutAt: string;
  eligibleForPaymentAt: DateString;
  signingUrl?: string;
  totalLoanAmount: number;
  numberOfRequiredGuarantors: number;
}

export interface IBids {
  id: string;
  recommendedLenderResponse?: { id: string };
  lenderResponses: ILenderResponse[];
}
